import type { CSSProperties } from 'react';
import type { Severity } from '@soc/shared';
import { sevColor } from '../../lib/severity';
import { RuleIcon } from '../../lib/ruleIcons';

/**
 * Alert pin: twin sonar rings radiating outward (severity-colored, for every
 * severity) + a clickable severity-colored disc bearing the firing rule's glyph.
 * Operators read severity (color) and which rule fired (icon) straight off the
 * map; clicking opens the anchored detail popup. Rendered centered on a Marker.
 */
export function Pin({
  severity,
  ruleId,
  delay = 0,
  onSelect,
}: {
  severity: Severity;
  ruleId: string;
  delay?: number;
  onSelect?: () => void;
}) {
  const color = sevColor(severity);
  const ring: CSSProperties = {
    left: -13,
    top: -13,
    width: 26,
    height: 26,
    ['--ring-color' as string]: color,
  };

  return (
    <div style={{ position: 'relative', width: 0, height: 0 }}>
      <span className="pin-wave" style={{ ...ring, animationDelay: `${delay}s` }} />
      <span className="pin-wave pin-wave--2" style={{ ...ring, animationDelay: `${delay + 1.3}s` }} />
      <span
        role="button"
        tabIndex={0}
        aria-label={`${severity} alert — open details`}
        onClick={onSelect}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelect?.();
          }
        }}
        style={{
          position: 'absolute',
          left: -11,
          top: -11,
          width: 22,
          height: 22,
          borderRadius: '50%',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color,
          background: 'var(--surface-app)',
          border: `2px solid ${color}`,
          boxShadow: `0 0 6px ${color}`,
          cursor: 'pointer',
        }}
      >
        <RuleIcon ruleId={ruleId} size={12} />
      </span>
    </div>
  );
}
